import { Injectable } from '@angular/core';
import { combineLatest, fromEvent, Observable } from 'rxjs';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { debounceTime, distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { AuthService } from '../auth.service';
import { AppInsightsServiceDirective } from '../app-insights.service';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { ILogger } from '@app/shared/models/AppInsights/ILogger';

@Injectable({
  providedIn: 'root',
})
export class FeatureActivityLoggerService {
  constructor(
    private router: Router,
    private auth: AuthService,
    private appInsights: AppInsightsServiceDirective,
  ) {}

  initialize = () => {
    this.onNavigationEnd(this.router.events)
      .pipe(tap(() => this.log(this.getLogDetails(this.auth))))
      .subscribe(() => {
        combineLatest([this.onMouseClick(), this.onMouseMove()])
          .pipe(
            debounceTime(1000),
            map(() => this.getLogDetails(this.auth)),
            distinctUntilChanged(),
          )
          .subscribe((x) => this.log(x, this.appInsights));
      });
  };

  onMouseMove = () => fromEvent(document, 'mousemove');

  onMouseClick = () => fromEvent(document, 'click');

  onVisibilityChange = () => fromEvent(document, 'visibilitychange');

  onNavigationStart = (routerEvent: Observable<any> = this.router.events) => routerEvent.pipe(filter((event) => event instanceof NavigationStart));

  onNavigationEnd = (routerEvent: Observable<any> = this.router.events) => routerEvent.pipe(filter((event) => event instanceof NavigationEnd));

  getUrlPath = (router: Router = this.router) => router.url.split(';')[0].split('?')[0];

  getActivityId = (auth: AuthService = this.auth) => `${auth.sessionId}-${new Date().getTime()}`;

  getFeatureName = () => this.getLastPath(decodeURIComponent(this.getUrlPath()));

  getUser = (auth: AuthService = this.auth) => auth.currentUser;

  getLastPath = (path: string) => path.substring(path.lastIndexOf('/') + 1);

  getFullUrl = (router: Router = this.router) => router.url;

  log = (properties: any, logger: ILogger = this.appInsights) => logger.logEvent(ApplicationInsightEvent.featureActivity, properties);

  getLogDetails = (auth: AuthService) => ({
    ActivityId: this.getActivityId(auth),
    Feature: this.getFeatureName(),
    Url: this.getFullUrl(),
  });
}
